<script lang="ts">
  import { onMount } from 'svelte';
  import { refreshAll, refreshNvrs, selectedNvr } from './lib/stores';
  import { deleteNvr, listArchive, logout, patchNvr, whoami } from './lib/api';
  import type { NvrInfo, StreamInfo } from './lib/types';
  import Sidebar from './components/Sidebar.svelte';
  import Header from './components/Header.svelte';
  import Grid from './components/Grid.svelte';
  import NvrFormModal from './components/NvrFormModal.svelte';
  import PlaybackModal from './components/PlaybackModal.svelte';
  import ClipModal from './components/ClipModal.svelte';
  import ArchiveModal from './components/ArchiveModal.svelte';
  import Login from './components/Login.svelte';
  import UsersModal from './components/UsersModal.svelte';
  import PasswordModal from './components/PasswordModal.svelte';

  let me = $state<string | null>(null);
  let myRole = $state('viewer');
  // Пароль выдан админом: до его замены не пускаем к камерам.
  let mustChange = $state(false);
  let showUsers = $state(false);
  let showPassword = $state(false);
  let authChecked = $state(false);

  let modal = $state<
    | null
    | { kind: 'add' }
    | { kind: 'edit'; nvr: NvrInfo }
    | { kind: 'playback'; stream: StreamInfo }
    | { kind: 'clip'; stream: StreamInfo }
    | { kind: 'archive'; stream: StreamInfo; fromMs: number; toMs: number }
  >(null);

  let nvrMenu = $state<{ nvr: NvrInfo; left: number; top: number } | null>(null);

  let fullscreenName = $state<string | null>(null);
  let fullscreenLabel = $state('');
  let gridRef: Grid | undefined = $state();

  let pollTimer: ReturnType<typeof setInterval> | null = null;
  function startPolling(): void {
    refreshAll();
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = setInterval(refreshNvrs, 5000);
  }
  function stopPolling(): void {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }
  async function logoutAndReset(): Promise<void> {
    try {
      await logout();
    } catch (_) {
      /* ignore */
    }
    stopPolling();
    me = null;
    myRole = 'viewer';
    mustChange = false;
    showUsers = false;
    showPassword = false;
  }

  onMount(async () => {
    try {
      const w = await whoami();
      if (w) {
        me = w.username;
        myRole = w.role;
        mustChange = w.must_change;
        startPolling();
      }
    } catch (_) {
      /* network blip — show login */
    }
    authChecked = true;
    return () => stopPolling();
  });

  function openMenu(nvr: NvrInfo, anchor: HTMLElement): void {
    const r = anchor.getBoundingClientRect();
    nvrMenu = { nvr, left: r.right - 180, top: r.bottom + 4 };
  }

  async function rename(nvr: NvrInfo): Promise<void> {
    const newName = prompt('Новое имя рега:', nvr.name);
    nvrMenu = null;
    if (!newName || newName === nvr.name) return;
    try {
      await patchNvr(nvr.id, { name: newName });
      refreshAll();
    } catch (e) {
      alert('Не сохранилось: ' + (e instanceof Error ? e.message : e));
    }
  }

  async function del(nvr: NvrInfo): Promise<void> {
    nvrMenu = null;
    if (!confirm(`Удалить рег "${nvr.name}"?\nКонфиг почистится, RTSP-задачи остановятся.`)) return;
    try {
      await deleteNvr(nvr.id);
      if ($selectedNvr === nvr.id) $selectedNvr = '';
      refreshAll();
    } catch (e) {
      alert('Не удалить: ' + (e instanceof Error ? e.message : e));
    }
  }
</script>

<svelte:window
  onclick={(e) => {
    if (nvrMenu && !(e.target as HTMLElement).closest('.popup')) nvrMenu = null;
  }}
/>

{#if !authChecked}
  <div class="loading">Загрузка…</div>
{:else if me === null}
  <Login
    onsuccess={(w) => {
      me = w.username;
      myRole = w.role;
      mustChange = w.must_change;
      startPolling();
    }}
  />
{:else}
<div class="app" class:fullscreen={fullscreenName !== null}>
  <Sidebar
    onaddnvr={() => (modal = { kind: 'add' })}
    onnvrmenu={(nvr, anchor) => openMenu(nvr, anchor)}
  />
  <Header
    fullscreen={fullscreenName !== null}
    {fullscreenLabel}
    onback={() => {
      fullscreenName = null;
      fullscreenLabel = '';
    }}
  />
  <main>
    <Grid
      bind:this={gridRef}
      bind:fullscreenName
      onfullscreenchange={(name) => {
        fullscreenName = name;
        fullscreenLabel = name ?? '';
      }}
      onarchiveopen={(s) => (modal = { kind: 'playback', stream: s })}
      onclipopen={(s) => (modal = { kind: 'clip', stream: s })}
    />
  </main>
</div>

{#if nvrMenu}
  <div class="popup" style:left="{nvrMenu.left}px" style:top="{nvrMenu.top}px">
    <button class="item" onclick={() => rename(nvrMenu!.nvr)}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>Переименовать</button>
    <button class="item"
            onclick={() => { const n = nvrMenu!.nvr; nvrMenu = null; modal = { kind: 'edit', nvr: n }; }}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="3" />
      </svg>Настройки рега</button>
    <div class="sep"></div>
    <button class="item danger" onclick={() => del(nvrMenu!.nvr)}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
      </svg>Удалить</button>
  </div>
{/if}

{#if modal?.kind === 'add'}
  <NvrFormModal
    mode="add"
    onsuccess={(id) => { modal = null; refreshAll().then(() => $selectedNvr = id); }}
    onclose={() => (modal = null)}
  />
{:else if modal?.kind === 'edit'}
  <NvrFormModal
    mode="edit"
    nvr={modal.nvr}
    onsuccess={() => { modal = null; refreshAll(); }}
    onclose={() => (modal = null)}
  />
{:else if modal?.kind === 'playback'}
  {@const s = modal.stream}
  <PlaybackModal
    streamLabel={s.label || s.name}
    onsubmit={async (start, end) => {
      const fromMs = new Date(start).getTime();
      const toMs = new Date(end).getTime();
      // Свой архив глубиной около четырёх суток. Если период в него попал —
      // играем с диска: мгновенная перемотка и любые скорости. Что старше,
      // берём у регистратора: он хранит дольше, но отдаёт в реальном времени.
      let local = false;
      try {
        const hours = await listArchive(s.name);
        local = hours.some((h) => h.end_ms >= fromMs && h.start_ms <= toMs);
      } catch (_) {
        /* архив не ответил — уходим на регистратор */
      }
      if (local) {
        modal = { kind: 'archive', stream: s, fromMs, toMs };
      } else {
        await gridRef!.startArchive(s, start, end);
        modal = null;
      }
    }}
    onclose={() => (modal = null)}
  />
{:else if modal?.kind === 'archive'}
  {@const a = modal}
  <ArchiveModal
    streamName={a.stream.name}
    streamLabel={a.stream.label || a.stream.name}
    fromMs={a.fromMs}
    toMs={a.toMs}
    onclose={() => (modal = null)}
  />
{:else if modal?.kind === 'clip'}
  {@const c = modal.stream}
  <ClipModal
    streamName={c.name}
    streamLabel={c.label || c.name}
    onclose={() => (modal = null)}
  />
{/if}

  {#if myRole === 'admin'}
    <button class="tool-fab users" type="button" title="Пользователи" onclick={() => (showUsers = true)}>
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" />
        <path d="M16 3.13a4 4 0 010 7.75" />
      </svg>
    </button>
  {/if}
  <button class="tool-fab pw" type="button" title="Сменить пароль" onclick={() => (showPassword = true)}>
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  </button>
  <button class="logout-fab" type="button" title="Выйти ({me})" onclick={logoutAndReset}>
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  </button>

  {#if showUsers}
    <UsersModal
      me={me}
      onclose={() => (showUsers = false)}
      onloggedout={() => {
        showUsers = false;
        logoutAndReset();
      }}
    />
  {/if}

  {#if mustChange || showPassword}
    <PasswordModal
      forced={mustChange}
      onsuccess={() => {
        mustChange = false;
        showPassword = false;
      }}
      onclose={() => (showPassword = false)}
    />
  {/if}
{/if}

<style>
  .loading {
    position: fixed; inset: 0;
    display: grid; place-items: center;
    color: var(--fg-muted); font-size: 14px;
  }
  .logout-fab {
    position: fixed; right: 14px; top: 10px;
    width: 28px; height: 28px;
    border-radius: 50%;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    color: var(--fg-muted);
    display: grid; place-items: center;
    z-index: 50;
  }
  .logout-fab:hover { color: var(--bad); border-color: var(--bad); }
  .tool-fab {
    position: fixed; top: 10px;
    width: 28px; height: 28px;
    border-radius: 50%;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    color: var(--fg-muted);
    display: grid; place-items: center;
    z-index: 50;
  }
  .tool-fab:hover { color: var(--fg); }
  .tool-fab.pw { right: 48px; }
  .tool-fab.users { right: 82px; }
  .app {
    display: grid;
    grid-template-columns: var(--sidebar-w-expanded) 1fr;
    grid-template-rows: var(--header-h) 1fr;
    grid-template-areas: "sidebar header" "sidebar main";
    height: 100dvh;
    transition: grid-template-columns .25s cubic-bezier(.2,.8,.2,1);
  }
  :global(body.sidebar-collapsed) .app { grid-template-columns: var(--sidebar-w-collapsed) 1fr; }
  main { grid-area: main; overflow: auto; }

  @media (max-width: 700px) {
    .app { grid-template-columns: 0 1fr; }
    :global(body.sidebar-open) .app { grid-template-columns: var(--sidebar-w-expanded) 1fr; }
    :global(body.sidebar-collapsed) .app { grid-template-columns: 0 1fr; }
  }

  .popup {
    position: fixed; z-index: 200;
    min-width: 180px; padding: 6px;
    background: var(--bg);
    border: 1px solid var(--border); border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0,0,0,.18);
    display: flex; flex-direction: column; gap: 2px;
  }
  .popup .item {
    display: flex; align-items: center; gap: 8px;
    padding: 7px 10px; border-radius: 6px;
    font-size: 13px; color: var(--fg);
    text-align: left;
  }
  .popup .item:hover { background: rgba(127,127,127,.10); }
  .popup .item.danger { color: var(--bad); }
  .popup .item :global(svg) { width: 14px; height: 14px; flex-shrink: 0; opacity: .7; }
  .popup .sep { height: 1px; background: var(--border); margin: 4px 0; }
</style>
